import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  InlineLoading,
  Search,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Renew, Restart } from '@carbon/icons-react';
import { api, Permission, Submission, SubmissionDetail, SubmissionStatus } from '../lib/api';
import { formatDateTime, formatNumber, formatPeriod, relativeTime } from '../lib/format';
import { DEFAULT_PAGE_SIZE, lastPageIndex } from '../lib/pagination';
import { Card, Loading, Modal, StatusPill, TablePagination, useToast } from '../components/ui';

const STATUSES: Array<{ value: SubmissionStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'sent', label: 'Delivered' },
  { value: 'partial', label: 'Partial' },
  { value: 'unmapped', label: 'Unmapped' },
  { value: 'failed', label: 'Failed' },
  { value: 'received', label: 'Pending' },
];

export function Submissions({ can }: { can: (permission: Permission) => boolean }) {
  const [rows, setRows] = useState<Submission[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<SubmissionStatus | ''>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number | null>(null);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const latestRequest = useRef(0);
  const toast = useToast();

  const canRetry = can('submissions.retry');

  const load = useCallback(async () => {
    const request = ++latestRequest.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(page * pageSize),
      });
      if (status) params.set('status', status);
      if (search.trim()) params.set('search', search.trim());
      const result = await api.get<{ rows: Submission[]; total: number }>(
        `/api/submissions?${params.toString()}`,
      );
      // Typing in the search box fires a request per keystroke; only the newest may land.
      if (request !== latestRequest.current) return;
      setTotal(result.total);
      const lastPage = lastPageIndex(result.total, pageSize);
      if (page > lastPage) {
        // A retry moved rows out of the current filter and emptied this page; show the last real one.
        setPage(lastPage);
        return;
      }
      setRows(result.rows);
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, [page, pageSize, status, search, toast]);

  useEffect(() => {
    load();
  }, [load]);

  async function retryOne(id: number) {
    setRetrying(id);
    try {
      const outcome = await api.post<{ status: string; valueCount: number }>(
        `/api/submissions/${id}/retry`,
      );
      toast(
        outcome.status === 'sent' ? 'ok' : 'info',
        outcome.status === 'sent'
          ? `Delivered ${outcome.valueCount} values to DHIS2`
          : `Retry finished with status "${outcome.status}"`,
      );
      await load();
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      setRetrying(null);
    }
  }

  async function retryAll() {
    setBulkBusy(true);
    try {
      const result = await api.post<{ attempted: number; succeeded: number }>(
        '/api/submissions/retry-failed',
      );
      toast(
        result.succeeded === result.attempted ? 'ok' : 'info',
        `Retried ${result.attempted}; ${result.succeeded} delivered`,
      );
      await load();
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <>
      <div className="submissions-toolbar">
        <div className="submissions-toolbar-left">
          <Select
            id="submissions-status-filter"
            labelText="Filter by status"
            hideLabel
            size="md"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as SubmissionStatus | '');
              setPage(0);
            }}
            className="submissions-status-select"
          >
            {STATUSES.map((option) => (
              <SelectItem key={option.value} value={option.value} text={option.label} />
            ))}
          </Select>

          <Search
            id="submissions-search"
            labelText="Search facility or report id"
            placeholder="Search facility or report id"
            size="md"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            onClear={() => {
              setSearch('');
              setPage(0);
            }}
            className="submissions-search-input"
          />
        </div>

        <div className="submissions-toolbar-right">
          <Button
            kind="ghost"
            size="md"
            renderIcon={Renew}
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>

          {canRetry ? (
            <Button
              kind="primary"
              size="md"
              renderIcon={Restart}
              onClick={retryAll}
              disabled={bulkBusy}
            >
              Retry all failed
            </Button>
          ) : null}
        </div>
      </div>

      <Card bodyless>
        <div className="submissions-results-panel">
          {loading && rows.length === 0 ? (
            <div className="submissions-results-empty">
              <InlineLoading description="Loading submissions…" />
            </div>
          ) : rows.length === 0 ? (
            <div className="submissions-results-empty">
              <div className="submissions-empty-title">No submissions match</div>
              <div className="submissions-empty-desc">
                Facilities post to <span className="mono">/api/ingest/MeasureReport</span> with their API
                key. Nothing has arrived that matches this filter.
              </div>
            </div>
          ) : (
            <div className="table-wrap">
              <Table size="sm">
                <TableHead>
                  <TableRow>
                    <TableHeader>Facility</TableHeader>
                    <TableHeader>Period</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader className="num">Values</TableHeader>
                    <TableHeader className="num">Attempts</TableHeader>
                    <TableHeader>DHIS2 server</TableHeader>
                    <TableHeader>Received</TableHeader>
                    <TableHeader />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <div className="stack">
                          <span style={{ fontWeight: 560 }}>{row.facilityName}</span>
                          <span className="faint small mono">{row.facilityCode}</span>
                        </div>
                      </TableCell>
                      <TableCell>{formatPeriod(row.period)}</TableCell>
                      <TableCell>
                        <div className="stack">
                          <StatusPill status={row.status} />
                          {row.warningCount > 0 ? (
                            <span className="faint small">{row.warningCount} warnings</span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="num">{formatNumber(row.valueCount)}</TableCell>
                      <TableCell className="num">{row.attempts}</TableCell>
                      <TableCell className="muted">{row.targetName ?? '—'}</TableCell>
                      <TableCell className="muted" title={formatDateTime(row.receivedAt)}>
                        {relativeTime(row.receivedAt)}
                      </TableCell>
                      <TableCell>
                        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                          <Button
                            kind="ghost"
                            size="sm"
                            onClick={() => setSelected(row.id)}
                          >
                            Details
                          </Button>
                          {canRetry && row.status !== 'sent' ? (
                            <Button
                              kind="tertiary"
                              size="sm"
                              onClick={() => retryOne(row.id)}
                              disabled={retrying === row.id}
                            >
                              {retrying === row.id ? <span className="spinner" /> : null}
                              Retry
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {rows.length > 0 ? (
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalItems={total}
              onChange={(nextPage, nextPageSize) => {
                setPage(nextPage);
                setPageSize(nextPageSize);
              }}
            />
          ) : null}
        </div>
      </Card>

      {selected !== null ? (
        <SubmissionDetailModal id={selected} onClose={() => setSelected(null)} />
      ) : null}
    </>
  );
}

interface Attempt {
  id: number;
  attemptedAt: string;
  triggeredBy: string;
  success: boolean;
  statusCode: number | null;
  imported: number | null;
  updated: number | null;
  ignored: number | null;
  error: string | null;
}

function SubmissionDetailModal({ id, onClose }: { id: number; onClose: () => void }) {
  const [detail, setDetail] = useState<(SubmissionDetail & { attempts: Attempt[] }) | null>(null);
  const [tab, setTab] = useState<'summary' | 'payload' | 'source'>('summary');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<SubmissionDetail & { attempts: Attempt[] }>(`/api/submissions/${id}`)
      .then(setDetail)
      .catch((caught) => setError(caught instanceof Error ? caught.message : String(caught)));
  }, [id]);

  return (
    <Modal
      wide
      title={detail ? `${detail.facilityName} · ${formatPeriod(detail.period)}` : 'Submission'}
      onClose={onClose}
      footer={
        <Button kind="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {error ? <div className="banner banner-danger">{error}</div> : null}
      {!detail && !error ? <Loading /> : null}

      {detail ? (
        <>
          <div className="tabs" style={{ alignSelf: 'flex-start' }}>
            <button className={`tab${tab === 'summary' ? ' active' : ''}`} onClick={() => setTab('summary')}>
              Summary
            </button>
            <button className={`tab${tab === 'payload' ? ' active' : ''}`} onClick={() => setTab('payload')}>
              DHIS2 payload
            </button>
            <button className={`tab${tab === 'source' ? ' active' : ''}`} onClick={() => setTab('source')}>
              Source report
            </button>
          </div>

          {tab === 'summary' ? (
            <>
              {detail.error ? <div className="banner banner-danger">{detail.error}</div> : null}

              <div className="grid grid-stats">
                <div className="stack">
                  <span className="faint small">Status</span>
                  <StatusPill status={detail.status} />
                </div>
                <div className="stack">
                  <span className="faint small">Values delivered</span>
                  <strong>{formatNumber(detail.valueCount)}</strong>
                </div>
                <div className="stack">
                  <span className="faint small">Organisation unit</span>
                  <span className="mono">{detail.orgUnit ?? '—'}</span>
                </div>
                <div className="stack">
                  <span className="faint small">Report id</span>
                  <span className="mono truncate" title={detail.reportId}>
                    {detail.reportId}
                  </span>
                </div>
              </div>

              {detail.warnings.length > 0 ? (
                <div>
                  <div className="card-title" style={{ marginBottom: 8 }}>
                    Warnings ({detail.warnings.length})
                  </div>
                  <div className="code-block">
                    {detail.warnings.map((warning, index) => `• ${warning.message}`).join('\n')}
                  </div>
                </div>
              ) : null}

              <div>
                <div className="card-title" style={{ marginBottom: 8 }}>
                  Delivery attempts
                </div>
                {detail.attempts.length === 0 ? (
                  <div className="muted small">No attempt has been recorded yet.</div>
                ) : (
                  <div className="table-wrap">
                    <Table size="sm">
                      <TableHead>
                        <TableRow>
                          <TableHeader>When</TableHeader>
                          <TableHeader>Triggered by</TableHeader>
                          <TableHeader>Result</TableHeader>
                          <TableHeader className="num">Imported</TableHeader>
                          <TableHeader className="num">Updated</TableHeader>
                          <TableHeader className="num">Ignored</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.attempts.map((attempt) => (
                          <TableRow key={attempt.id}>
                            <TableCell className="muted">{formatDateTime(attempt.attemptedAt)}</TableCell>
                            <TableCell className="muted">{attempt.triggeredBy}</TableCell>
                            <TableCell>
                              <span className={`pill ${attempt.success ? 'pill-sent' : 'pill-failed'}`}>
                                {attempt.success ? 'OK' : `Failed${attempt.statusCode ? ` (${attempt.statusCode})` : ''}`}
                              </span>
                            </TableCell>
                            <TableCell className="num">{attempt.imported ?? '—'}</TableCell>
                            <TableCell className="num">{attempt.updated ?? '—'}</TableCell>
                            <TableCell className="num">{attempt.ignored ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </>
          ) : null}

          {tab === 'payload' ? (
            <div className="code-block">
              {detail.dataValueSet
                ? JSON.stringify(detail.dataValueSet, null, 2)
                : 'This submission has not been translated yet.'}
            </div>
          ) : null}

          {tab === 'source' ? (
            <div className="code-block">{JSON.stringify(detail.measureReport, null, 2)}</div>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}
